/**
 * Aperçu mobile : iframe vers la route de capture du marketing
 * (`/capture/showcase`) — la MÊME interface que le worker enregistre via
 * Playwright. Rejoue le showcase player de l'attempt sélectionné.
 *
 * L'iframe peut être capricieuse en dev (cross-origin, dev server marketing non
 * lancé). On fournit toujours un lien « ouvrir dans un onglet » en repli.
 */

import { cn } from '@jeraisonne/ui';
import { ExternalLink } from 'lucide-react';

const MARKETING_URL =
  (import.meta.env.VITE_MARKETING_URL as string | undefined) ?? 'https://jeraisonne.fr';

interface CapturePreviewProps {
  exerciseId?: string | null;
  attemptId?: string | null;
  className?: string;
}

export function captureUrl(opts: {
  exerciseId?: string | null;
  attemptId?: string | null;
  /** Mode iframe sans cadre (aperçu admin) — l'écran remplit l'iframe. */
  embed?: boolean;
}): string {
  const params = new URLSearchParams();
  if (opts.attemptId) params.set('attempt', opts.attemptId);
  else if (opts.exerciseId) params.set('exercise', opts.exerciseId);
  if (opts.embed) params.set('embed', '1');
  return `${MARKETING_URL}/capture/showcase?${params.toString()}`;
}

export function CapturePreview({ exerciseId, attemptId, className }: CapturePreviewProps) {
  if (!exerciseId && !attemptId) {
    return (
      <div
        className={cn(
          'border-ink-200 text-ink-400 aspect-9/19 flex w-full items-center justify-center rounded-2xl border border-dashed text-center text-xs',
          className,
        )}
      >
        Choisis une réponse (attempt)
        <br />
        pour l’aperçu mobile
      </div>
    );
  }
  const url = captureUrl({ exerciseId, attemptId });
  return (
    <div className="space-y-2">
      <div
        className={cn(
          'border-ink-900 aspect-9/19 rounded-4xl mx-auto w-full max-w-[240px] overflow-hidden border-[6px] bg-white shadow-lg',
          className,
        )}
      >
        <iframe
          title="Aperçu mobile"
          src={url}
          className="h-full w-full"
          sandbox="allow-scripts allow-same-origin"
        />
      </div>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="text-primary-600 mx-auto flex w-fit items-center gap-1 text-xs font-bold hover:underline"
      >
        <ExternalLink className="h-3.5 w-3.5" /> Ouvrir l’aperçu dans un onglet
      </a>
    </div>
  );
}
